import { X, Filter, Search, Send } from "lucide-react";
import { ToggleSwitch } from "../../../ui/ToggleSwitch";
import { Tooltip } from "@/ui-components/ui/tooltip";

interface CompactControlsProps {
  mode: "search" | "chat";
  value: string;
  shouldExpand: boolean;
  hasFilters: boolean;
  filterCount: number;
  onModeChange: (mode: "search" | "chat") => void;
  onSubmit: () => void;
  onClear: () => void;
  onToggleFilters: () => void;
  onFocusTextarea: () => void;
}

/**
 * Control buttons for CompactUnifiedSearch
 * Renders different layouts based on expanded/compact state
 */
export function CompactControls({
  mode,
  value,
  shouldExpand,
  hasFilters,
  filterCount,
  onModeChange,
  onSubmit,
  onClear,
  onToggleFilters,
  onFocusTextarea,
}: CompactControlsProps) {
  const hasValue = value.trim().length > 0;

  // Expanded mode controls
  if (shouldExpand) {
    return (
      <div className="flex items-start gap-2 ml-2">
        {value && (
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onClear();
              onFocusTextarea();
            }}
            className="p-1.5 rounded-full hover:bg-muted transition-all"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}

        <div onMouseDown={(e) => e.preventDefault()}>
          <ToggleSwitch
            leftLabel="Chat"
            rightLabel="Search"
            value={mode === "chat" ? "left" : "right"}
            onChange={(val) => onModeChange(val === "left" ? "chat" : "search")}
          />
        </div>

        <div className="w-px h-6 bg-border mx-2" />

        <Tooltip
          content={mode === "search" ? "Search" : "Send message"}
          side="bottom"
        >
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={onSubmit}
            disabled={!hasValue}
            className={`
              px-3 py-1.5 rounded-lg transition-all shadow-sm
              ${
                hasValue
                  ? "bg-[#01B9D9] hover:bg-[#0095B6] text-white hover:shadow-md"
                  : "bg-muted text-muted-foreground"
              }
            `}
          >
            {mode === "search" ? (
              <Search className="w-3.5 h-3.5" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
          </button>
        </Tooltip>
      </div>
    );
  }

  // Compact mode controls
  return (
    <>
      {/* Clear button */}
      {value && (
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onClear();
            onFocusTextarea();
          }}
          className="mr-1 p-1.5 rounded-full hover:bg-muted transition-all"
        >
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      )}

      {/* Search filter button */}
      {mode === "search" && (
        <Tooltip content="Search filters" side="bottom">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={onToggleFilters}
            className={`
              mr-1 p-1.5 rounded-full text-xs flex items-center gap-1 transition-all
              ${
                hasFilters
                  ? "bg-[#01B9D9]/10 text-[#01B9D9] hover:bg-[#01B9D9]/20"
                  : "text-muted-foreground hover:bg-muted"
              }
            `}
          >
            <Filter className="w-3.5 h-3.5" />
            {hasFilters && (
              <span className="bg-[#01B9D9] text-white text-[10px] px-1 rounded-full min-w-[14px] h-3.5 flex items-center justify-center">
                {filterCount}
              </span>
            )}
          </button>
        </Tooltip>
      )}

      {/* Mode toggle */}
      <div onMouseDown={(e) => e.preventDefault()}>
        <ToggleSwitch
          leftLabel="Chat"
          rightLabel="Search"
          value={mode === "chat" ? "left" : "right"}
          onChange={(val) => onModeChange(val === "left" ? "chat" : "search")}
        />
      </div>

      <div className="w-px h-6 bg-gray-200 mx-2" />

      {/* Submit button */}
      <Tooltip
        content={mode === "search" ? "Search" : "Send message"}
        side="bottom"
      >
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={onSubmit}
          disabled={!hasValue}
          className={`
            mr-2 px-3 py-1.5 rounded-lg transition-all shadow-sm
            ${
              hasValue
                ? "bg-[#01B9D9] hover:bg-[#0095B6] text-white hover:shadow-md"
                : "bg-gray-100 text-gray-500"
            }
          `}
        >
          {mode === "search" ? (
            <Search className="w-3.5 h-3.5" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
        </button>
      </Tooltip>
    </>
  );
}